import { useQuery } from '@apollo/client';
import { GET_AUTO_GENERATE_DRAFT_DATA } from '@zaplify/graphql';
import { useEffect, useMemo, useRef } from 'react';

export const useAutoGenerateDraft = ({
    contactId,
    regenerateMessage,
}: {
    contactId: string;
    regenerateMessage: ({
        feedback,
        path,
        autoGenerate,
    }: {
        feedback?: string;
        path?: string;
        autoGenerate?: boolean;
    }) => void;
}) => {
    'use no memo';
    const { data, loading } = useQuery(GET_AUTO_GENERATE_DRAFT_DATA, {
        variables: { contactId },
        skip: !contactId,
    });
    const hasAutoGeneratedDraft = useRef(false);

    const shouldGenerateDraft = useMemo(() => {
        if (loading) return false;
        if (hasAutoGeneratedDraft.current) return false;
        hasAutoGeneratedDraft.current = true;

        const contact = data.Contacts_by_pk;
        const latestMessage = contact.latestMessage?.[0];
        const latestDraft = contact.latestDraft?.[0];

        // Helper function to check if a draft should be considered archived
        const isDraftArchived = (draft: typeof latestDraft) => {
            if (!draft) return false;

            // If source is 'auto-generate', consider it archived after 24 hours
            if (draft.source === 'auto-generate') {
                const createdAt = new Date(draft.createdAt);
                const now = new Date();
                const timeDifference = now.getTime() - createdAt.getTime();
                const hoursElapsed = timeDifference / (1000 * 60 * 60);
                return hoursElapsed >= 24;
            }

            return false;
        };

        // Case 1: No messages and no drafts - generate draft
        if (!latestMessage && !latestDraft) {
            return true;
        }

        // Case 2: No draft exists but there are messages - generate draft
        if (!latestDraft && latestMessage) {
            return true;
        }

        // Case 3: No messages but draft exists and is not archived - don't generate
        if (!latestMessage && latestDraft && !isDraftArchived(latestDraft)) {
            return false;
        }

        // Case 4: No messages but draft exists and is archived - generate draft
        if (!latestMessage && latestDraft && isDraftArchived(latestDraft)) {
            return true;
        }

        // Case 5: Both exist - check if draft is newer than the latest message or if draft is archived
        if (latestMessage?.sentOn && latestDraft?.updatedAt) {
            const messageDate = new Date(latestMessage.sentOn);
            const draftDate = new Date(latestDraft.updatedAt);

            // Generate draft if the latest message is newer than the draft OR if the draft is archived
            return messageDate > draftDate || isDraftArchived(latestDraft);
        }

        return false;
    }, [data]);

    useEffect(() => {
        console.log('shouldGenerateDraft', shouldGenerateDraft);
        if (shouldGenerateDraft) {
            regenerateMessage({ autoGenerate: true });
        }
    }, [shouldGenerateDraft]);

    return {
        shouldGenerateDraft,
        loading,
    };
};
